/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'

// The aperture at the end of the corridor. Ported from V1's library: the white
// circle must read as a TUNNEL MOUTH, not a sun — so the white is recessed behind
// a 12 m barrel whose walls brighten with depth (the light lives inside), with
// faint rib rings for the one-point-perspective depth cue. The corridor seam
// lights (tubesData.ts) converge on this rim to finish the read.
// MeshBasic / unfogged on purpose — the deep white must outshine --ground.

const DEPTH = 12
const R = 3.6

const BARREL_VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`
const BARREL_FRAG = /* glsl */ `
varying vec2 vUv;
void main() {
  float depth = 1.0 - vUv.y; // 0 at the mouth, 1 at the deep end
  // walls brighten toward the source: the glow comes FROM the tunnel
  vec3 col = mix(vec3(0.862, 0.895, 0.917), vec3(1.0), smoothstep(0.1, 0.82, depth));
  // rib rings receding inside — the depth cue that kills the flat-disc read
  float f = fract(depth * 5.0);
  float rib = 1.0 - 0.07 * (1.0 - smoothstep(0.0, 0.12, min(f, 1.0 - f))) * (1.0 - depth);
  gl_FragColor = vec4(col * rib, 1.0);
}
`

export function Aperture() {
  const built = useMemo(() => {
    // barrel axis along local z, mouth at z=0, deep end at z=-DEPTH
    const barrelGeo = new THREE.CylinderGeometry(R, R, DEPTH, 48, 1, true)
    barrelGeo.rotateX(Math.PI / 2)
    barrelGeo.translate(0, 0, -DEPTH / 2)
    const barrelMat = new THREE.ShaderMaterial({
      vertexShader: BARREL_VERT,
      fragmentShader: BARREL_FRAG,
      side: THREE.BackSide,
    })
    // outer mass so the tunnel reads as a block from above during the ascent,
    // not a hollow film
    const shellGeo = new THREE.CylinderGeometry(R + 0.22, R + 0.22, DEPTH, 48, 1, true)
    shellGeo.rotateX(Math.PI / 2)
    shellGeo.translate(0, 0, -DEPTH / 2)
    const shellMat = new THREE.MeshBasicMaterial({ color: '#E8EEF1' })
    const capMat = new THREE.MeshBasicMaterial({ color: '#FFFFFF' })
    const rimMat = new THREE.MeshBasicMaterial({
      color: '#F8FBFC',
      transparent: true,
      opacity: 0.55,
    })
    return { barrelGeo, barrelMat, shellGeo, shellMat, capMat, rimMat }
  }, [])

  return (
    // y such that the mouth's lower edge sits just below the floor — a tunnel
    // opens FROM the ground; floating above it was part of the sun read
    <group position={[0, 3.45, 113.5]} rotation-y={Math.PI}>
      <mesh geometry={built.shellGeo} material={built.shellMat} />
      <mesh geometry={built.barrelGeo} material={built.barrelMat} />
      {/* the source: pure --white, at the far end of the barrel, not at the mouth */}
      <mesh position-z={-DEPTH + 0.05} material={built.capMat}>
        <circleGeometry args={[R, 48]} />
      </mesh>
      {/* thin rim so the burn diffuses onto the surrounding architecture —
          deliberately smaller than before: the corona was what made it a sun */}
      <mesh position-z={0.02} material={built.rimMat}>
        <ringGeometry args={[R, R + 0.9, 48]} />
      </mesh>
    </group>
  )
}
